'use client'

import { LOGIN_COUNTRY_COOKIE_KEY } from '@shared/config/auth'

import {
  EFFECTIVE_LOCALE_COOKIE_KEY,
  LANGUAGE_CODE_COOKIE_KEY,
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  SYSTEM_LOCALE_COOKIE_KEY,
  type LanguageCode,
  type Locale,
} from './locales'
import { normalizeLanguageCode, resolveEffectiveLocale } from './resolve'

const getCookieValue = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? decodeURIComponent(match[2]) : undefined
}

export const getBrowserSystemLocale = (): string | null => {
  if (typeof navigator === 'undefined') {
    return null
  }

  return navigator.languages?.join(',') || navigator.language || null
}

export const getBrowserLanguageCode = (): LanguageCode => {
  if (typeof document === 'undefined') {
    return 'system'
  }

  return normalizeLanguageCode(getCookieValue(LANGUAGE_CODE_COOKIE_KEY))
}

export const getBrowserLoginCountry = (): string | null => {
  if (typeof document === 'undefined') {
    return null
  }

  return getCookieValue(LOGIN_COUNTRY_COOKIE_KEY) ?? null
}

export const getBrowserEffectiveLocale = (): Locale => {
  if (typeof document === 'undefined') {
    return 'en'
  }

  return resolveEffectiveLocale({
    languageCode: getBrowserLanguageCode(),
    loginCountry: getBrowserLoginCountry(),
    systemLocale: getCookieValue(SYSTEM_LOCALE_COOKIE_KEY) ?? getBrowserSystemLocale(),
  })
}

export const persistBrowserLocaleCookies = ({
  languageCode,
  systemLocale,
  effectiveLocale,
}: {
  languageCode: LanguageCode
  systemLocale: string | null
  effectiveLocale: Locale
}) => {
  document.cookie = `${LANGUAGE_CODE_COOKIE_KEY}=${encodeURIComponent(languageCode)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`
  if (systemLocale) {
    document.cookie = `${SYSTEM_LOCALE_COOKIE_KEY}=${encodeURIComponent(systemLocale)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`
  }
  document.cookie = `${EFFECTIVE_LOCALE_COOKIE_KEY}=${encodeURIComponent(effectiveLocale)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`
}
